import { useEffect, useRef, useState } from 'react';

/*
 * Decides whether a loading placeholder is worth showing.
 *
 * Two failure modes this exists to prevent, both of which make an app feel
 * *slower* than simply doing nothing:
 *
 * 1. The flash. A response that arrives in 90ms still swaps the UI twice —
 *    content → skeleton → content — and the eye reads that blink as jank. So
 *    nothing appears until the request has been outstanding for `delay`. Most
 *    warm requests never reach it and the table simply updates.
 *
 * 2. The flicker. If a skeleton does appear and the response lands 40ms later,
 *    it vanishes before it can be recognised as loading. Once shown, it stays
 *    for at least `minDuration`.
 *
 * Net effect: fast requests show nothing, slow ones show a stable placeholder,
 * and neither produces a visible blink. Timings are the usual perception
 * thresholds — under ~150ms reads as instant, and ~400ms is about the shortest
 * a state can be shown and still register.
 */
export const useDeferredLoading = (
  isLoading,
  { delay = 150, minDuration = 400 } = {},
) => {
  const [isVisible, setVisible] = useState(false);
  const shownAtRef = useRef(0);

  useEffect(() => {
    if (isLoading) {
      // Already showing — let it run rather than restarting the clock.
      if (isVisible) return undefined;
      const timer = setTimeout(() => {
        shownAtRef.current = Date.now();
        setVisible(true);
      }, delay);
      return () => clearTimeout(timer);
    }

    if (!isVisible) return undefined;

    const elapsed = Date.now() - shownAtRef.current;
    const timer = setTimeout(
      () => setVisible(false),
      Math.max(0, minDuration - elapsed),
    );
    return () => clearTimeout(timer);
  }, [isLoading, isVisible, delay, minDuration]);

  return isVisible;
};

export default useDeferredLoading;
